import {createAction} from '@packages/utilities/create-action';
import type {PageRecordType} from '@packages/systems/core/records/PageRecord';
import type {Locale} from '@packages/systems/localization-types';
import {
  BRANCH_CREATE_STARTED,
  BRANCH_CREATE_SUCCEEDED,
  BRANCH_CREATE_FAILED,
  BRANCH_MERGE_INITIATED,
  BRANCH_MERGE_STARTED,
  BRANCH_MERGE_SUCCEEDED,
  BRANCH_MERGE_CONFLICTED,
  BRANCH_MERGE_FAILED,
  BRANCH_MERGE_CANCELLED,
  BRANCH_MERGE_RESOLUTIONS_SUBMITTED,
  BRANCH_UPDATE_INITIATED,
  BRANCH_UPDATE_STARTED,
  BRANCH_UPDATE_SUCCEEDED,
  BRANCH_UPDATE_CONFLICTED,
  BRANCH_UPDATE_FAILED,
  BRANCH_UPDATE_SKIPPED,
  BRANCH_UPDATE_CANCELLED,
  BRANCH_UPDATE_RESOLUTIONS_SUBMITTED,
  BRANCH_DELETE_STARTED,
  BRANCH_DELETE_SUCCEEDED,
  BRANCH_DELETE_FAILED,
  BRANCH_DELETE_CANCELLED,
  BRANCH_REDIRECT_OPENED,
  BRANCH_REDIRECT_DEFERRED,
  BRANCH_REDIRECT_COMPLETED,
  SITE_PUBLISH_TARGET_SET,
} from '@packages/systems/designer/actions';
import type {BranchErrorAction, ConflictType} from './types';

export type BranchCreateRequestArgs = {
  meta: Record<string, unknown>;
  locale?: Locale;
};

export const branchCreateStarted = createAction<
  typeof BRANCH_CREATE_STARTED,
  {
    pageId: string;
    userId: string | null;
    requestArgs?: BranchCreateRequestArgs;
  }
>(BRANCH_CREATE_STARTED);

export const branchCreateSucceeded = createAction<
  typeof BRANCH_CREATE_SUCCEEDED,
  {
    pageId: string;
    branchId: string;
    page: PageRecordType;
    meta: Record<string, unknown>;
  }
>(BRANCH_CREATE_SUCCEEDED);

export const branchCreateFailed = createAction<
  typeof BRANCH_CREATE_FAILED,
  {
    pageId: string;
  } & BranchErrorAction
>(BRANCH_CREATE_FAILED);

export type BranchUpdateRequestArgs = {
  pageId?: string;
  resolutions?: Record<
    string,
    {
      id: string;
      resolution: 'main' | 'branch';
      type: 'style' | 'component';
    }
  >;
};

export const branchUpdateInitiated = createAction<
  typeof BRANCH_UPDATE_INITIATED,
  {
    branchId: string;
    pageId: string;
    userId: string | null;
  }
>(BRANCH_UPDATE_INITIATED);

export const branchUpdateStarted = createAction<
  typeof BRANCH_UPDATE_STARTED,
  {
    branchId: string;
    pageId: string;
    userId: string | null;
    requestArgs?: BranchUpdateRequestArgs;
  }
>(BRANCH_UPDATE_STARTED);

export const branchUpdateSucceeded = createAction<
  typeof BRANCH_UPDATE_SUCCEEDED,
  {
    pageId: string;
    branchId: string;
  }
>(BRANCH_UPDATE_SUCCEEDED);

export const branchUpdateFailed = createAction<
  typeof BRANCH_UPDATE_FAILED,
  {
    branchId: string;
  } & BranchErrorAction
>(BRANCH_UPDATE_FAILED);

export const branchUpdateConflicted = createAction<
  typeof BRANCH_UPDATE_CONFLICTED,
  {
    pageId: string;
    branchId: string;
    styles: Array<ConflictType>;
    symbols: Array<ConflictType>;
  }
>(BRANCH_UPDATE_CONFLICTED);

export const branchUpdateSkipped = createAction<
  typeof BRANCH_UPDATE_SKIPPED,
  {
    branchId: string;
  }
>(BRANCH_UPDATE_SKIPPED);

export const branchUpdateCancelled = createAction<
  typeof BRANCH_UPDATE_CANCELLED,
  {
    branchId: string;
  }
>(BRANCH_UPDATE_CANCELLED);

export const branchUpdateResolutionsSubmitted = createAction<
  typeof BRANCH_UPDATE_RESOLUTIONS_SUBMITTED,
  {
    branchId: string;
    pageId: string;
    userId: string | null;
    resolutions: NonNullable<BranchUpdateRequestArgs['resolutions']>;
  }
>(BRANCH_UPDATE_RESOLUTIONS_SUBMITTED);

export const branchMergeInitiated = createAction<
  typeof BRANCH_MERGE_INITIATED,
  {
    branchId: string;
    pageId: string;
    userId: string | null;
  }
>(BRANCH_MERGE_INITIATED);

export const branchMergeStarted = createAction<
  typeof BRANCH_MERGE_STARTED,
  {
    branchId: string;
    pageId: string;
    userId: string | null;
  }
>(BRANCH_MERGE_STARTED);

export const branchMergeSucceeded = createAction<
  typeof BRANCH_MERGE_SUCCEEDED,
  {
    pageId: string;
    branchId?: string;
  }
>(BRANCH_MERGE_SUCCEEDED);

export const branchMergeConflicted = createAction<
  typeof BRANCH_MERGE_CONFLICTED,
  {
    pageId: string;
    branchId: string;
    styles: Array<ConflictType>;
    symbols: Array<ConflictType>;
  }
>(BRANCH_MERGE_CONFLICTED);

export const branchMergeFailed = createAction<
  typeof BRANCH_MERGE_FAILED,
  {
    branchId?: string;
    switchToBranchPageId?: string;
  } & BranchErrorAction
>(BRANCH_MERGE_FAILED);

export const branchMergeCancelled = createAction<
  typeof BRANCH_MERGE_CANCELLED,
  {
    branchId?: string;
  }
>(BRANCH_MERGE_CANCELLED);

export const branchMergeResolutionsSubmitted = createAction<
  typeof BRANCH_MERGE_RESOLUTIONS_SUBMITTED,
  {
    branchId: string;
    pageId: string;
    userId: string | null;
    resolutions: NonNullable<BranchUpdateRequestArgs['resolutions']>;
  }
>(BRANCH_MERGE_RESOLUTIONS_SUBMITTED);

export const branchDeleteStarted = createAction<
  typeof BRANCH_DELETE_STARTED,
  {
    branchId: string;
    pageId: string;
    userId: string | null;
    requestArgs?: {pageId: string};
    panelName?: string;
  }
>(BRANCH_DELETE_STARTED);

export const branchDeleteSucceeded = createAction<
  typeof BRANCH_DELETE_SUCCEEDED,
  {
    pageId: string;
    branchId?: string;
  }
>(BRANCH_DELETE_SUCCEEDED);

export const branchDeleteFailed = createAction<
  typeof BRANCH_DELETE_FAILED,
  {
    branchId: string;
  } & BranchErrorAction
>(BRANCH_DELETE_FAILED);

export const branchDeleteCancelled = createAction<
  typeof BRANCH_DELETE_CANCELLED,
  {
    branchId: string;
  }
>(BRANCH_DELETE_CANCELLED);

export type BranchRedirectReason = 'merge' | 'delete';

export const branchRedirectOpened = createAction<
  typeof BRANCH_REDIRECT_OPENED,
  {
    reason: BranchRedirectReason;
    branchId: string;
    userId: string | null;
    deferredPageId?: string | null;
  }
>(BRANCH_REDIRECT_OPENED);

export const branchRedirectDeferred = createAction<
  typeof BRANCH_REDIRECT_DEFERRED,
  {
    pageId: string;
  }
>(BRANCH_REDIRECT_DEFERRED);

export const branchRedirectCompleted = createAction<
  typeof BRANCH_REDIRECT_COMPLETED
>(BRANCH_REDIRECT_COMPLETED);

// Records which branch the publish this tab initiated targets (`null` =
// full-site), so the branch UI can tell its own staging publish apart from a
// full-site/other-branch publish it is only blocked by (WORK-1811).
export const sitePublishTargetSet = createAction<
  typeof SITE_PUBLISH_TARGET_SET,
  {branchId: string | null}
>(SITE_PUBLISH_TARGET_SET);
